import type { ReactElement } from 'react';
import type { ExtensionRiskEstimate } from '@kirin/api-client/contracts/extensions';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import {
  formatRiskLabel,
  riskEstimateLabel,
  riskLabelSeverity,
} from './agentmesh-risk-labels';

interface RiskEstimateCellProps {
  /** Optional on list rows until enrichment runs; null = not assessed. */
  riskEstimate: ExtensionRiskEstimate | undefined;
}

/**
 * Risk Estimate cell for inventory extension rows (KIR-1928) — the overall
 * AgentMesh score, styled like the Alerts severity column. The label is the
 * raw AgentMesh vocabulary (level, falling back to status); colors are a
 * best-effort map of known strings, neutral for unknown vocabulary.
 */
export function RiskEstimateCell({
  riskEstimate,
}: RiskEstimateCellProps): ReactElement {
  const label = riskEstimateLabel(riskEstimate);
  if (label === null) {
    return (
      <span className="inline-flex items-center gap-1.5 text-muted-foreground">
        <span
          aria-hidden
          className="size-1.5 shrink-0 rounded-full bg-muted-foreground/50"
        />
        Not assessed
      </span>
    );
  }

  const formatted = formatRiskLabel(label);
  return (
    <span className="inline-block max-w-full truncate" title={formatted}>
      <SeverityBadge
        riskLevel={riskLabelSeverity(label)}
        label={formatted}
        showIcon={false}
      />
    </span>
  );
}

RiskEstimateCell.displayName = 'RiskEstimateCell';
